/**
 * Alert + reunification snapshot fetch.
 *
 * Migrated to typed `@tiaki/http-client` per ADR 113 §Decision.
 */
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

/**
 * Minimal-typed AlertHistory shape — the registry handler only reads `id`
 * and passes the rest through as an opaque payload. The full DRF
 * AlertHistorySocketSerializer field set stays on the wire.
 */
export interface SnapshotAlertHistory {
  id: number;
  [key: string]: unknown;
}

export interface SnapshotRoomStatus {
  id: number;
  alert_id?: number | null;
  [key: string]: unknown;
}

export interface SnapshotReunificationEntity {
  id: string;
  [key: string]: unknown;
}

export interface SnapshotReunificationEvent
  extends SnapshotReunificationEntity {
  school?: number;
  status?: string;
}

export interface AlertSnapshot {
  active_alerts: SnapshotAlertHistory[];
  room_statuses: SnapshotRoomStatus[];
  reunification_event: SnapshotReunificationEvent | null;
  reunification_students: SnapshotReunificationEntity[];
  reunification_guardians: SnapshotReunificationEntity[];
  reunification_staff: SnapshotReunificationEntity[];
  server_ts: string;
}

/**
 * Fetch the unified state-recovery snapshot.
 *
 * When `schoolId` is null the backend returns the union of every school
 * the caller belongs to.
 */
export async function fetchAlertSnapshot(
  schoolId: number | null,
): Promise<AlertSnapshot> {
  const result = await client.GET('/api/v2/alerts/snapshot/', {
    params: {
      query: (schoolId != null ? { school_id: schoolId } : {}) as never,
    },
  });
  return unwrap(result) as unknown as AlertSnapshot;
}
